import { useState } from 'react';
import PropTypes from 'prop-types';
import { X, Globe, Hash, ToggleLeft, Save, Plus, Sparkles, AlertCircle } from 'lucide-react';
import Button from './ui/Button';
import templateAdminService from '../services/templateAdminService';
import './TemplateEditorModal.css';

/**
 * Supported locales for question template translations.
 * Matches the ZoomClient's 20 supported languages.
 */
const LOCALES = [
    { code: 'en',    label: 'English',                required: true  },
    { code: 'ar',    label: 'Arabic',                 required: false },
    { code: 'de',    label: 'German',                 required: false },
    { code: 'es',    label: 'Spanish',                required: false },
    { code: 'fr',    label: 'French',                 required: false },
    { code: 'hi',    label: 'Hindi',                  required: false },
    { code: 'id',    label: 'Indonesian',             required: false },
    { code: 'it',    label: 'Italian',                required: false },
    { code: 'ja',    label: 'Japanese',               required: false },
    { code: 'ko',    label: 'Korean',                 required: false },
    { code: 'nl',    label: 'Dutch',                  required: false },
    { code: 'pl',    label: 'Polish',                 required: false },
    { code: 'pt-BR', label: 'Portuguese (Brazil)',    required: false },
    { code: 'ru',    label: 'Russian',                required: false },
    { code: 'sv',    label: 'Swedish',                required: false },
    { code: 'th',    label: 'Thai',                   required: false },
    { code: 'tr',    label: 'Turkish',                required: false },
    { code: 'vi',    label: 'Vietnamese',             required: false },
    { code: 'zh-CN', label: 'Chinese (Simplified)',   required: false },
    { code: 'zh-TW', label: 'Chinese (Traditional)', required: false },
];

/** snake_case validation pattern */
const SNAKE_CASE_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * Build the initial translations state from a template (or empty for create mode).
 */
const buildInitialTranslations = (template) => {
    const trans = {};
    LOCALES.forEach(({ code }) => {
        trans[code] = (template && template.translations && template.translations[code]) || '';
    });
    return trans;
};

/**
 * TemplateEditorModal
 *
 * Modal for creating and editing question templates.
 * Create mode: template prop is null.
 * Edit mode:   template prop is the existing template document.
 */
const TemplateEditorModal = ({ template, onSave, onClose }) => {
    const isEditMode = template !== null && template !== undefined;

    const [templateKey, setTemplateKey]     = useState(template ? template.templateKey : '');
    const [isSummary,   setIsSummary]       = useState(template ? template.isSummary : false);
    const [isActive,    setIsActive]        = useState(template ? template.isActive : true);
    const [rank,        setRank]            = useState(template ? template.rank : 1);
    const [translations, setTranslations]  = useState(buildInitialTranslations(template));
    const [errors,      setErrors]          = useState({});
    const [isSubmitting, setIsSubmitting]  = useState(false);
    const [submitError, setSubmitError]    = useState('');
    const [isGenerating, setIsGenerating]  = useState(false);
    const [generateError, setGenerateError] = useState('');
    const [generatedFields, setGeneratedFields] = useState(new Set());

    const validate = () => {
        const newErrors = {};

        if (!isEditMode) {
            if (!templateKey.trim()) {
                newErrors.templateKey = 'Template key is required';
            } else if (!SNAKE_CASE_PATTERN.test(templateKey)) {
                newErrors.templateKey = 'Template key must be snake_case (e.g. action_items)';
            }
        }

        if (!translations.en.trim()) {
            newErrors.translationEn = 'English translation is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        setSubmitError('');
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                templateKey: isEditMode ? template.templateKey : templateKey.trim(),
                category: 'global',
                rank: Number(rank),
                isActive,
                isSummary,
                translations,
            };

            if (isEditMode) {
                await templateAdminService.updateTemplate(template.templateKey, payload);
            } else {
                await templateAdminService.createTemplate(payload);
            }

            onSave();
        } catch (err) {
            setSubmitError(err.message || 'Failed to save template');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTranslationChange = (code, value) => {
        setTranslations(prev => ({ ...prev, [code]: value }));
    };

    const handleGenerateTranslations = async () => {
        setGenerateError('');
        setGeneratedFields(new Set());

        // Collect empty language codes (skip English)
        const emptyLanguages = LOCALES
            .filter(({ code }) => code !== 'en' && !translations[code].trim())
            .map(({ code }) => code);

        if (emptyLanguages.length === 0) {
            setGenerateError('All translation fields are already filled');
            return;
        }

        setIsGenerating(true);
        try {
            const result = await templateAdminService.generateTranslations(
                translations.en,
                emptyLanguages
            );

            // Merge results into translations state (only fill empty fields)
            const filled = new Set();
            setTranslations(prev => {
                const updated = { ...prev };
                for (const [code, text] of Object.entries(result)) {
                    if (!updated[code].trim()) {
                        updated[code] = text;
                        filled.add(code);
                    }
                }
                return updated;
            });

            setGeneratedFields(filled);

            // Clear animation after 1.5s
            setTimeout(() => setGeneratedFields(new Set()), 1500);
        } catch (err) {
            setGenerateError(err.message || 'Failed to generate translations');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div
            className="modal-overlay"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={isEditMode ? 'Edit template' : 'Create template'}
        >
            <div
                className="template-editor-modal"
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div className="tem-header">
                    <div className="tem-header-left">
                        <div className="tem-icon-wrap">
                            {isEditMode ? <Save size={18} /> : <Plus size={18} />}
                        </div>
                        <div>
                            <h3 className="tem-title">
                                {isEditMode ? 'Edit Template' : 'Create Template'}
                            </h3>
                            <p className="tem-subtitle">
                                {isEditMode
                                    ? `Editing: ${template.templateKey}`
                                    : 'Add a new question template to the Meeting AI panel'}
                            </p>
                        </div>
                    </div>
                    <button
                        className="tem-close-btn"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="tem-body">
                    {/* Submit-level error */}
                    {submitError && (
                        <div className="tem-submit-error" role="alert">
                            {submitError}
                        </div>
                    )}

                    {/* ── Core fields ── */}
                    <section className="tem-section">
                        <div className="tem-section-label">
                            <Hash size={14} />
                            Identity
                        </div>

                        {/* Template Key */}
                        <div className="tem-field-group">
                            <label htmlFor="tem-key" className="tem-label">
                                Template Key
                                {!isEditMode && <span className="tem-required">*</span>}
                            </label>
                            {isEditMode ? (
                                <input
                                    id="tem-key"
                                    className="tem-input tem-input-disabled"
                                    value={templateKey}
                                    disabled
                                    aria-label="Template key"
                                />
                            ) : (
                                <>
                                    <input
                                        id="tem-key"
                                        className={`tem-input${errors.templateKey ? ' tem-input-err' : ''}`}
                                        value={templateKey}
                                        onChange={e => setTemplateKey(e.target.value)}
                                        placeholder="e.g. action_items"
                                        aria-label="Template key"
                                        spellCheck={false}
                                    />
                                    {errors.templateKey && (
                                        <span className="tem-field-error">{errors.templateKey}</span>
                                    )}
                                </>
                            )}
                            <p className="tem-hint">
                                Unique identifier. Use snake_case. Cannot be changed after creation.
                            </p>
                        </div>

                        {/* Rank */}
                        <div className="tem-field-group">
                            <label htmlFor="tem-rank" className="tem-label">Rank</label>
                            <input
                                id="tem-rank"
                                type="number"
                                min="0"
                                className="tem-input tem-input-narrow"
                                value={rank}
                                onChange={e => setRank(e.target.value)}
                                aria-label="Rank"
                            />
                            <p className="tem-hint">Display order (0 = top). Summary template stays at rank 0.</p>
                        </div>
                    </section>

                    {/* ── Flags ── */}
                    <section className="tem-section">
                        <div className="tem-section-label">
                            <ToggleLeft size={14} />
                            Settings
                        </div>

                        <div className="tem-toggles-row">
                            <label className="tem-checkbox-label">
                                <input
                                    type="checkbox"
                                    className="tem-checkbox"
                                    checked={isActive}
                                    onChange={e => setIsActive(e.target.checked)}
                                    aria-label="Is Active"
                                />
                                <span className="tem-checkbox-text">Is Active</span>
                                <span className="tem-checkbox-hint">Visible to hosts & participants</span>
                            </label>

                            <label className="tem-checkbox-label">
                                <input
                                    type="checkbox"
                                    className="tem-checkbox"
                                    checked={isSummary}
                                    onChange={e => setIsSummary(e.target.checked)}
                                    aria-label="Is Summary"
                                />
                                <span className="tem-checkbox-text">Is Summary</span>
                                <span className="tem-checkbox-hint">Triggers meeting summary instead of Q&A</span>
                            </label>
                        </div>
                    </section>

                    {/* ── Translations ── */}
                    <section className="tem-section">
                        <div className="tem-section-label tem-section-label-with-action">
                            <div className="tem-section-label-left">
                                <Globe size={14} />
                                Translations
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                icon={<Sparkles size={14} />}
                                onClick={handleGenerateTranslations}
                                disabled={!translations.en.trim() || isGenerating || isSubmitting}
                                isLoading={isGenerating}
                                className="tem-generate-btn"
                                data-testid="generate-translations-btn"
                            >
                                {isGenerating ? 'Generating...' : 'Auto-fill'}
                            </Button>
                        </div>

                        {generateError && (
                            <div className="tem-generate-error" role="alert">
                                <AlertCircle size={14} />
                                {generateError}
                            </div>
                        )}

                        {errors.translationEn && (
                            <div className="tem-field-error tem-field-error-block">
                                {errors.translationEn}
                            </div>
                        )}

                        <div className="tem-translations-grid">
                            {LOCALES.map(({ code, label, required }) => (
                                <div key={code} className={`tem-locale-item${required ? ' tem-locale-required' : ''}`}>
                                    <label
                                        htmlFor={`tem-trans-${code}`}
                                        className="tem-locale-label"
                                    >
                                        <span className="tem-locale-code">{code}</span>
                                        <span className="tem-locale-name">{label}</span>
                                        {required && <span className="tem-required">*</span>}
                                    </label>
                                    <input
                                        id={`tem-trans-${code}`}
                                        type="text"
                                        className={`tem-input${required && errors.translationEn ? ' tem-input-err' : ''}${generatedFields.has(code) ? ' tem-input-generated' : ''}`}
                                        value={translations[code]}
                                        onChange={e => handleTranslationChange(code, e.target.value)}
                                        placeholder={required ? 'Required' : 'Optional'}
                                        data-testid={`translation-${code}`}
                                        aria-label={`${label} translation`}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                {/* ── Footer ── */}
                <div className="tem-footer">
                    <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        isLoading={isSubmitting}
                        data-testid="editor-submit-btn"
                    >
                        {isEditMode ? 'Save Changes' : 'Create Template'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

TemplateEditorModal.propTypes = {
    template: PropTypes.shape({
        templateKey: PropTypes.string,
        category:    PropTypes.string,
        rank:        PropTypes.number,
        isActive:    PropTypes.bool,
        isSummary:   PropTypes.bool,
        translations: PropTypes.objectOf(PropTypes.string),
        usageCount:  PropTypes.number,
        type:        PropTypes.string,
        createdAt:   PropTypes.string,
        updatedAt:   PropTypes.string,
    }),
    onSave:  PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

TemplateEditorModal.defaultProps = {
    template: null,
};

export default TemplateEditorModal;
